import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { FiFilter, FiSearch, FiChevronDown } from 'react-icons/fi'
import SocietyCard from './SocietyCard'

interface SocietiesData {
  allMdx: {
    nodes: Array<{
      id: string
      frontmatter: {
        title: string
        image?: string
        tags?: string[]
      }
      fields: {
        slug: string
      }
    }>
  }
}

const SocietiesList: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const data = useStaticQuery<SocietiesData>(graphql`
    query SocietiesListQuery {
      allMdx(
        filter: { internal: { contentFilePath: { regex: "/content/societies/" } } }
        sort: { frontmatter: { title: ASC } }
      ) {
        nodes {
          id
          frontmatter {
            title
            image
            tags
          }
          fields {
            slug
          }
        }
      }
    }
  `)

  const societies = data.allMdx.nodes

  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    societies.forEach((society) => {
      society.frontmatter.tags?.forEach((tag) => tagSet.add(tag))
    })
    return Array.from(tagSet).sort()
  }, [societies])

  const filteredSocieties = useMemo(() => {
    return societies.filter((society) => {
      const matchesSearch = searchQuery === '' ||
        society.frontmatter.title.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesTags = selectedTags.size === 0 ||
        (society.frontmatter.tags &&
         society.frontmatter.tags.some(tag => selectedTags.has(tag)))

      return matchesSearch && matchesTags
    })
  }, [societies, searchQuery, selectedTags])

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(tag)) {
        newSet.delete(tag)
      } else {
        newSet.add(tag)
      }
      return newSet
    })
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedTags(new Set())
    setIsDropdownOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  if (societies.length === 0) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-semibold text-text-light mb-4">
          No societies found
        </h2>
        <p className="text-text-light">
          Societies will appear here once they're added to the system.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search societies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 pl-10 bg-gray-100 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-colors"
          />
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
        </div>

        {allTags.length > 0 && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 min-w-[140px] justify-between"
            >
              <div className="flex items-center gap-2">
                <FiFilter className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700 font-medium">
                  Filter {selectedTags.size > 0 && `(${selectedTags.size})`}
                </span>
              </div>
              <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <div className="p-3 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Filter by tags</span>
                    {selectedTags.size > 0 && (
                      <button
                        onClick={() => setSelectedTags(new Set())}
                        className="text-xs text-primary hover:text-primary-dark"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto p-3">
                  {allTags.map((tag) => (
                    <label
                      key={tag}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTags.has(tag)}
                        onChange={() => toggleTag(tag)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">{tag}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {(selectedTags.size > 0 || searchQuery) && (
          <button
            onClick={clearFilters}
            className="px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors text-gray-700 font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {filteredSocieties.length === 0 ? (
        <div className="text-center py-16">
          <h2 className="text-2xl font-semibold text-text-light mb-4">
            No societies match your filters
          </h2>
          <p className="text-text-light mb-6">
            Try adjusting your search or filter criteria.
          </p>
          <button
            onClick={clearFilters}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSocieties.map((society) => (
            <SocietyCard
              key={society.id}
              title={society.frontmatter.title}
              image={society.frontmatter.image}
              slug={society.fields.slug}
              tags={society.frontmatter.tags}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default SocietiesList